// S9 — one tenant's health (spec 15 §4 S9, §5 `GET /v1/platform/tenants/:id/
// health`; M15-4, D-611, D-612). The latest window's readings, the last 24
// hours as four strips (reachability, error rate, p95, outbox lag), the last
// 30 days of uptime from the daily rollups, the tenant's alerts, the fleet's
// backup reading, and, for a platform_admin on a sandbox tenant, the health
// drill.
//
// A window that was not collected is a grey cell, never a green one (spec 15
// §2.21). The restore drill and the audit chain have no source yet (D-484,
// D-485 ruling 4), so they are not on this screen (D-033).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import { DataTable, type DataTableColumn } from "../../../components/data-table";
import { Modal } from "../../../components/modal";
import { CardSkeleton, EmptyState, ErrorState } from "../../../components/states";
import { StatusChip } from "../../../components/status-chip";
import { Banner } from "../../../components/toast";
import { formatBusinessDate, formatTimestamp } from "../../../lib/datetime";
import { Fact, Facts } from "../components/facts";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import { HealthDot, HealthStrip } from "../components/health-dot";
import { useHeldKey, withHeldKey } from "../lib/held-key";
import {
  ageSeconds,
  canDrill,
  countTones,
  dayTone,
  errorTone,
  formatBp,
  formatMs,
  formatSeconds,
  lagTone,
  latencyTone,
  upTone,
  type Tone,
} from "../lib/health";
import {
  getTenant,
  getTenantHealth,
  PlatformError,
  setHealthDrill,
  type HealthAlert,
  type HealthSnapshot,
  type PlatformMe,
} from "../lib/platform-api";

export function TenantHealthScreen({ me, tenantId }: { me: PlatformMe; tenantId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = i18n.language;
  const dash = t("admin.health.none");
  const [confirm, setConfirm] = useState(false);

  const detail = useQuery({
    queryKey: ["platform", "tenant", tenantId],
    queryFn: ({ signal }) => getTenant(tenantId, signal),
  });
  const day = useQuery({
    queryKey: ["platform", "tenant", tenantId, "health", "24h"],
    queryFn: ({ signal }) => getTenantHealth(tenantId, "24h", signal),
    refetchInterval: 60_000,
  });
  const month = useQuery({
    queryKey: ["platform", "tenant", tenantId, "health", "30d"],
    queryFn: ({ signal }) => getTenantHealth(tenantId, "30d", signal),
  });
  useSignInAgainOn401(detail.error ?? day.error ?? month.error);

  const drillKey = useHeldKey();
  const drill = useMutation({
    mutationFn: (next: boolean) =>
      withHeldKey(drillKey, { health_drill: next }, (key) => setHealthDrill(tenantId, next, key)),
    onSuccess: async () => {
      setConfirm(false);
      await queryClient.invalidateQueries({ queryKey: ["platform", "tenant", tenantId] });
    },
  });
  useSignInAgainOn401(drill.error);

  const crumbs = [{ label: t("admin.tenant.back"), to: "/console/tenants" }];

  if (detail.isPending || day.isPending) {
    return (
      <section className="admin-screen" data-testid="admin-tenant-health-loading">
        <PageHeader title={t("admin.health.loading")} crumbs={crumbs} />
        <CardSkeleton lines={6} label={t("admin.health.loading")} />
      </section>
    );
  }
  if (detail.isError || day.isError) {
    const error = detail.error ?? day.error;
    const notFound =
      error instanceof PlatformError && (error.status === 404 || error.status === 400);
    return (
      <section className="admin-screen" data-testid="admin-tenant-health-error">
        <PageHeader
          title={notFound ? t("admin.tenant.notFoundTitle") : t("admin.load.failed")}
          crumbs={crumbs}
        />
        {notFound ? (
          <EmptyState
            title={t("admin.tenant.notFoundTitle")}
            body={t("admin.tenant.notFoundBody")}
            ctaLabel={t("admin.tenant.back")}
            onCta={() => {
              void navigate({ to: "/console/tenants" });
            }}
          />
        ) : (
          <ErrorState
            title={t("admin.load.failed")}
            retryLabel={t("admin.load.retry")}
            onRetry={() => {
              void detail.refetch();
              void day.refetch();
            }}
            requestId={requestIdOf(error)}
            requestIdLabel={t("admin.load.requestId")}
          />
        )}
      </section>
    );
  }

  const tenant = detail.data.tenant;
  const health = day.data;
  const current = health.current;
  const windows = health.windows;
  const now = new Date();

  function strip(
    label: string,
    tone: (s: HealthSnapshot) => Tone,
    value: (s: HealthSnapshot) => string | null,
    testId: string,
  ) {
    const tones = windows.map(tone);
    const counts = countTones(tones);
    return (
      <HealthStrip
        label={label}
        data-testid={testId}
        summary={t("admin.health.stripSummary", {
          bad: counts.bad,
          warn: counts.warn,
          none: counts.none,
          total: windows.length,
        })}
        cells={windows.map((s, i) => ({
          key: s.captured_at,
          tone: tones[i] ?? "none",
          title: `${formatTimestamp(s.captured_at, locale)} · ${value(s) ?? t("admin.health.notCollected")}`,
        }))}
      />
    );
  }

  const alertColumns: DataTableColumn<HealthAlert>[] = [
    {
      key: "kind",
      header: t("admin.health.colKind"),
      render: (a) => t(`admin.health.alertKind.${a.kind}`),
    },
    {
      key: "opened",
      header: t("admin.health.colOpened"),
      render: (a) => formatTimestamp(a.opened_at, locale),
    },
    {
      key: "state",
      header: t("admin.health.colState"),
      kind: "status",
      render: (a) =>
        a.resolved_at === null ? (
          <StatusChip variant="bad">{t("admin.health.alertOpen")}</StatusChip>
        ) : (
          <StatusChip variant="neutral">
            {t("admin.health.alertResolved", { time: formatTimestamp(a.resolved_at, locale) })}
          </StatusChip>
        ),
    },
    {
      key: "paged",
      header: t("admin.health.colPaged"),
      render: (a) => (a.paged ? t("admin.health.paged") : t("admin.health.held")),
    },
  ];

  const backupAge = ageSeconds(health.backup?.last_backup_at ?? null, now);
  const drillable = canDrill(me.role, tenant.sandbox);

  return (
    <section className="admin-screen" data-testid="admin-tenant-health">
      <PageHeader
        title={t("admin.health.tenantTitle", { name: tenant.name })}
        crumbs={[...crumbs, { label: tenant.name, to: `/console/tenants/${tenant.id}` }]}
        actions={<HealthDot health={health.health} data-testid="admin-tenant-health-dot" />}
      />
      {health.health_drill ? (
        <Banner kind="warn" data-testid="admin-tenant-health-drill-on">
          {t("admin.health.drillOn")}
        </Banner>
      ) : null}

      <div className="admin-detail-grid">
        <Card title={t("admin.health.now")} data-testid="admin-tenant-health-now">
          {current === null ? (
            <EmptyState
              title={t("admin.health.noReadingTitle")}
              body={t("admin.health.noReadingBody")}
            />
          ) : (
            <Facts>
              <Fact label={t("admin.health.probe")} data-testid="admin-tenant-health-probe">
                {current.subdomain_up === null
                  ? t("admin.health.notCollected")
                  : current.subdomain_up
                    ? t("admin.health.probeUp", { ms: formatMs(current.probe_ms, locale) ?? dash })
                    : t("admin.health.probeDown")}
              </Fact>
              <Fact label={t("admin.health.requests")}>
                {current.request_count === null ? dash : String(current.request_count)}
              </Fact>
              <Fact label={t("admin.health.colErrors")}>
                {current.request_count === 0
                  ? t("admin.health.noTraffic")
                  : (formatBp(current.http_error_rate_bp, locale) ?? dash)}
              </Fact>
              <Fact label={t("admin.health.colP95")}>
                {formatMs(current.p95_latency_ms, locale) ?? dash}
              </Fact>
              <Fact label={t("admin.health.colLag")}>
                {formatSeconds(current.queue_lag_seconds, locale) ?? dash}
              </Fact>
              <Fact label={t("admin.health.colParked")}>
                {current.queue_depth === null ? dash : String(current.queue_depth)}
              </Fact>
              <Fact label={t("admin.health.statBackup")}>
                {health.backup === null || health.backup.source === "off"
                  ? t("admin.health.backupOff")
                  : backupAge === null
                    ? t("admin.health.backupNone")
                    : (formatSeconds(backupAge, locale) ?? dash)}
              </Fact>
            </Facts>
          )}
        </Card>

        <Card title={t("admin.health.last24h")} data-testid="admin-tenant-health-strips">
          {windows.length === 0 ? (
            <EmptyState
              title={t("admin.health.noReadingTitle")}
              body={t("admin.health.noReadingBody")}
            />
          ) : (
            <div className="admin-strips">
              {strip(
                t("admin.health.stripUp"),
                upTone,
                (s) =>
                  s.subdomain_up === null
                    ? null
                    : s.subdomain_up
                      ? t("admin.health.up")
                      : t("admin.health.down"),
                "admin-health-strip-up",
              )}
              {strip(
                t("admin.health.colErrors"),
                errorTone,
                (s) =>
                  s.request_count === 0
                    ? t("admin.health.noTraffic")
                    : formatBp(s.http_error_rate_bp, locale),
                "admin-health-strip-errors",
              )}
              {strip(
                t("admin.health.colP95"),
                latencyTone,
                (s) => formatMs(s.p95_latency_ms, locale),
                "admin-health-strip-p95",
              )}
              {strip(
                t("admin.health.colLag"),
                lagTone,
                (s) => formatSeconds(s.queue_lag_seconds, locale),
                "admin-health-strip-lag",
              )}
            </div>
          )}
        </Card>
      </div>

      <Card title={t("admin.health.last30d")} data-testid="admin-tenant-health-days">
        {month.isPending ? (
          <CardSkeleton lines={1} label={t("admin.health.loading")} />
        ) : month.isError ? (
          <ErrorState
            title={t("admin.load.failed")}
            retryLabel={t("admin.load.retry")}
            onRetry={() => {
              void month.refetch();
            }}
            requestId={requestIdOf(month.error)}
            requestIdLabel={t("admin.load.requestId")}
          />
        ) : month.data.days.length === 0 ? (
          <p className="admin-hint">{t("admin.health.noDays")}</p>
        ) : (
          <HealthStrip
            label={t("admin.health.uptime")}
            data-testid="admin-health-strip-days"
            summary={t("admin.health.daysSummary", { count: month.data.days.length })}
            cells={month.data.days.map((d) => ({
              key: d.date,
              tone: dayTone(d),
              title: `${formatBusinessDate(d.date, locale)} · ${formatBp(d.uptime_bp, locale) ?? t("admin.health.notCollected")}`,
            }))}
          />
        )}
      </Card>

      <Card
        title={t("admin.health.alertsTitle")}
        count={health.recent_alerts.length}
        action={
          <Button
            size="sm"
            data-testid="admin-tenant-health-logs"
            onClick={() => {
              void navigate({ to: "/console/logs", search: { tenant: tenant.id } });
            }}
          >
            {t("admin.health.viewLogs")}
          </Button>
        }
        data-testid="admin-tenant-health-alerts"
      >
        {health.recent_alerts.length === 0 ? (
          <p className="admin-hint">{t("admin.health.noAlerts")}</p>
        ) : (
          <DataTable
            columns={alertColumns}
            rows={health.recent_alerts}
            rowKey={(a) => a.id}
            empty={null}
            caption={t("admin.health.alertsTitle")}
            rowTestId="admin-tenant-health-alert"
            rowProps={(a) => ({ "data-kind": a.kind })}
          />
        )}
      </Card>

      {drillable ? (
        <Card title={t("admin.health.drillTitle")} data-testid="admin-tenant-health-drill">
          <p className="admin-hint">{t("admin.health.drillBody")}</p>
          {drill.isError ? <Banner kind="bad">{t("admin.health.drillFailed")}</Banner> : null}
          <div className="admin-actions">
            <Button
              variant={health.health_drill ? "primary" : "danger"}
              size="sm"
              data-testid="admin-tenant-health-drill-toggle"
              onClick={() => {
                setConfirm(true);
              }}
            >
              {health.health_drill ? t("admin.health.drillStop") : t("admin.health.drillStart")}
            </Button>
          </div>
        </Card>
      ) : null}

      {confirm ? (
        <Modal
          label={health.health_drill ? t("admin.health.drillStop") : t("admin.health.drillStart")}
          heading={health.health_drill ? t("admin.health.drillStop") : t("admin.health.drillStart")}
          closeLabel={t("admin.health.cancel")}
          onClose={() => {
            setConfirm(false);
          }}
          pending={drill.isPending}
          testId="admin-tenant-health-drill-confirm"
          className="admin-modal"
        >
          <p className="admin-card-lead">
            {health.health_drill
              ? t("admin.health.drillStopBody")
              : t("admin.health.drillStartBody")}
          </p>
          <div className="admin-actions">
            <Button
              onClick={() => {
                setConfirm(false);
              }}
            >
              {t("admin.health.cancel")}
            </Button>
            <Button
              variant="primary"
              busy={drill.isPending}
              busyLabel={t("admin.health.saving")}
              data-testid="admin-tenant-health-drill-apply"
              onClick={() => {
                drill.mutate(!health.health_drill);
              }}
            >
              {t("admin.health.confirm")}
            </Button>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
